import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useCompare } from "../context/CompareContext";
import LouisianaMap from "../components/LouisianaMap";
import { AvailabilityBadge, FreshnessBadge } from "../components/StatusBadge";
import {
  Search, SlidersHorizontal, Phone, MapPin, Clock, Plus, Check, List, Map as MapIcon, X,
} from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function SearchDashboard() {
  const { ids, toggle } = useCompare();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const [asamLevels, setAsam] = useState([]);
  const [payers, setPayers] = useState([]);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showAdv, setShowAdv] = useState(false);
  const [mobileView, setMobileView] = useState("list");

  const [f, setF] = useState({
    population: "",
    asam_level: "",
    parish: "",
    available_now: false,
    include_stale: false,
    q: "",
    sex: "",
    pregnancy_parenting: false,
    moud: false,
    cooccurring: false,
    justice_involvement: false,
    accessibility: false,
    payer: "",
  });

  useEffect(() => {
    api.get("/asam-levels").then((r) => setAsam(r.data.filter((l) => l.status === "active")));
    api.get("/payers").then((r) => setPayers(r.data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v === true) params[k] = true;
      else if (v && v !== "") params[k] = v;
    });
    try {
      const { data } = await api.get("/search", { params });
      setResults(data.results);
      setCount(data.count);
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const Chip = ({ k, label }) => (
    <button
      data-testid={`filter-${k}`}
      onClick={() => set(k, !f[k])}
      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
        f[k] ? "border-teal-600 bg-teal-700 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      {preview && (
        <div data-testid="preview-banner" className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm">
          <span className="font-semibold text-teal-800">Preview — this is exactly what a referral-partner user sees.</span>
          <Link to="/admin" data-testid="exit-preview" className="rounded-lg bg-[#64b6ac] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#64b6ac]/90">Exit to administration</Link>
        </div>
      )}
      <div className="mb-2">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
          Where is a staffed bed available right now?
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Availability reflects each provider's most recent report and does not guarantee admission.
        </p>
      </div>

      {/* Primary filter bar */}
      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-3">
            <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Population</label>
            <select
              data-testid="filter-population"
              value={f.population}
              onChange={(e) => set("population", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-600"
            >
              <option value="">Adult & Adolescent</option>
              <option value="adult">Adult</option>
              <option value="adolescent">Adolescent</option>
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">ASAM level</label>
            <select
              data-testid="filter-asam"
              value={f.asam_level}
              onChange={(e) => set("asam_level", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-600"
            >
              <option value="">Any level</option>
              {asamLevels.map((l) => (
                <option key={l.code} value={l.code}>{`${l.code} — ${l.official_name}`}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Parish</label>
            <input
              data-testid="filter-parish"
              value={f.parish}
              onChange={(e) => set("parish", e.target.value)}
              placeholder="e.g. Orleans"
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-600"
            />
          </div>
          <div className="md:col-span-4">
            <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Provider / facility</label>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 focus-within:border-teal-600">
              <Search size={16} className="text-slate-400" />
              <input
                data-testid="filter-search"
                value={f.q}
                onChange={(e) => set("q", e.target.value)}
                placeholder="Search by name"
                className="w-full bg-transparent py-2.5 text-sm outline-none"
              />
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            data-testid="filter-available-now"
            onClick={() => set("available_now", !f.available_now)}
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              f.available_now ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Check size={14} /> Available now
          </button>
          <Chip k="include_stale" label="Include stale results" />
          <button
            data-testid="toggle-advanced"
            onClick={() => setShowAdv((s) => !s)}
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <SlidersHorizontal size={14} /> Advanced filters
          </button>
          <span className="ml-auto text-sm font-semibold text-slate-500">
            {loading ? "Searching…" : `${count} program${count === 1 ? "" : "s"}`}
          </span>
        </div>

        {showAdv && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Sex designation</label>
              <select
                data-testid="filter-sex"
                value={f.sex}
                onChange={(e) => set("sex", e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-600"
              >
                <option value="">Any</option>
                <option value="men">Men</option>
                <option value="women">Women</option>
                <option value="coed">Co-ed</option>
                <option value="flex">Flexible</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Payer accepted & open</label>
              <select
                data-testid="filter-payer"
                value={f.payer}
                onChange={(e) => set("payer", e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-600"
              >
                <option value="">Any payer</option>
                {payers.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Chip k="pregnancy_parenting" label="Pregnancy / parenting" />
              <Chip k="moud" label="MOUD/MAT" />
              <Chip k="cooccurring" label="Co-occurring" />
              <Chip k="justice_involvement" label="Justice involved" />
              <Chip k="accessibility" label="Accessibility" />
            </div>
          </div>
        )}
      </div>

      {/* Mobile view toggle */}
      <div className="mb-3 flex gap-2 lg:hidden">
        <button
          onClick={() => setMobileView("list")}
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${mobileView === "list" ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200 bg-white text-slate-600"}`}
        >
          <List size={16} /> List
        </button>
        <button
          onClick={() => setMobileView("map")}
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${mobileView === "map" ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200 bg-white text-slate-600"}`}
        >
          <MapIcon size={16} /> Map
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Results list */}
        <div className={`${mobileView === "map" ? "hidden" : "block"} lg:block space-y-3`} data-testid="results-list">
          {!loading && results.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
              No programs match your filters. Try enabling “Include stale results” or widening filters.
            </div>
          )}
          {results.map((r) => (
            <div
              key={r.id}
              data-testid={`result-card-${r.id}`}
              onMouseEnter={() => setSelectedId(r.id)}
              className={`rounded-xl border bg-white p-5 transition-colors ${selectedId === r.id ? "border-teal-500" : "border-slate-200"}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  {r.photo_url
                    ? <img src={r.photo_url} alt="" className="h-16 w-20 shrink-0 rounded-lg border border-slate-200 object-cover" />
                    : <div className="grid h-16 w-20 shrink-0 place-items-center rounded-lg bg-slate-100 text-[10px] text-slate-400">No photo</div>}
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{r.org_name}</div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{r.facility_name}</h3>
                  <div className="text-sm text-slate-600">{r.name}</div>
                  <div className="mt-1 text-xs font-semibold text-teal-700">
                    ASAM {r.asam_level_code} · {r.asam_official_name}
                  </div>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={`font-display text-5xl font-black leading-none ${r.available_beds > 0 && r.freshness !== "stale" ? "status-available" : "text-slate-400"}`}>
                    {r.available_beds}
                  </div>
                  <div className="text-xs font-semibold text-slate-500">beds available</div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <AvailabilityBadge beds={r.available_beds} freshness={r.freshness} testid={`avail-${r.id}`} />
                <FreshnessBadge status={r.freshness} testid={`fresh-${r.id}`} />
                <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 capitalize">
                  {r.population}
                </span>
                {r.transportation_status && r.transportation_status !== "unavailable" && (
                  <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600">
                    Transport: {r.transportation_status.replace(/_/g, " ")}
                  </span>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Clock size={13} /> Updated {timeAgo(r.last_updated)}</span>
                {r.parish && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {r.parish} Parish</span>}
                {r.intake_phone && <span className="inline-flex items-center gap-1"><Phone size={13} /> {r.intake_phone}</span>}
              </div>

              <div className="mt-4 flex items-center gap-2">
                <Link
                  to={`/programs/${r.id}`}
                  data-testid={`view-details-${r.id}`}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  View details
                </Link>
                <button
                  data-testid={`compare-toggle-${r.id}`}
                  onClick={() => toggle(r.id)}
                  className={`flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-semibold ${
                    ids.includes(r.id) ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {ids.includes(r.id) ? <Check size={15} /> : <Plus size={15} />}
                  {ids.includes(r.id) ? "In comparison" : "Add to comparison"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Map */}
        <div className={`${mobileView === "list" ? "hidden" : "block"} lg:block`}>
          <div className="sticky top-20 h-[70vh]">
            <LouisianaMap results={results} selectedId={selectedId} onSelect={setSelectedId} />
          </div>
        </div>
      </div>
    </div>
  );
}
